import { type ComponentProps, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Modal, useModals, useModalContext } from '../index'
import { useCreateSkill, useUpdateSkill, useForkSkill, useSkillById } from '@renderer/chat/hooks/useSkills'
import { usePlanFeatures } from '@renderer/chat/hooks/use-plan-features'
import { Input } from '@renderer/windows/components/input'
import { Textarea } from '@renderer/windows/components/textarea'
import { Button } from '@highlight/ui'
import { GitFork } from 'lucide-react'
import { twMerge } from 'tailwind-merge'
import type { Skill } from '@renderer/chat/types'

function FieldGroup({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <div>
        <div className="text-foreground-secondary text-sm font-medium">{label}</div>
        {hint && <p className="text-foreground-placeholder mt-0.5 text-xs">{hint}</p>}
      </div>
      {children}
    </div>
  )
}

function AutoResizeTextarea({ className, value, ...props }: ComponentProps<'textarea'>) {
  const ref = useRef<HTMLTextAreaElement>(null)

  const resize = useCallback(() => {
    const el = ref.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight}px`
  }, [])

  useEffect(() => {
    resize()
  }, [value, resize])

  return (
    <textarea
      ref={ref}
      value={value}
      className={twMerge(
        'text-md flex w-full min-w-0 resize-none overflow-hidden px-4 py-3 font-medium',
        'bg-background hover:border-hover rounded-lg border outline-none transition-[color,border-color]',
        'placeholder:text-foreground-disabled text-foreground-secondary',
        'focus-visible:border-[color:var(--focus-ring-color)]',
        'min-h-[120px]',
        className,
      )}
      {...props}
    />
  )
}

export const SkillEditorModal = () => {
  const { closeModal } = useModals()
  const { context } = useModalContext('skill-editor')

  const contextSkill = context?.skill as Skill | undefined
  const { data: fetchedSkill } = useSkillById(contextSkill?.id)
  const skill = fetchedSkill ?? contextSkill

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [skillContent, setSkillContent] = useState('')

  const createSkillMutation = useCreateSkill()
  const updateSkillMutation = useUpdateSkill()
  const forkSkillMutation = useForkSkill()
  const { createCustomSkills } = usePlanFeatures()

  const isEditingDefault = !!skill?.isDefault && !skill?.forkedFromId
  const isUpdate = !!skill?.id && !skill.isDefault

  useEffect(() => {
    if (!skill) {
      setName('')
      setDescription('')
      setSkillContent('')
      return
    }

    setName(skill.name ?? '')
    setDescription(skill.description ?? '')
    setSkillContent(skill.skillContent ?? skill.prompt ?? '')
  }, [skill?.id])

  const handleSave = () => {
    const trimmedName = name.trim()
    const trimmedDescription = description.trim()
    const trimmedSkillContent = skillContent.trim()

    if (isEditingDefault && skill?.id) {
      // Customizing a default materializes a user-owned fork — the same plan
      // gate as create, and the server's forkSkill refuses it anyway.
      if (!createCustomSkills) {
        closeModal('skill-editor')
        return
      }
      forkSkillMutation.mutate(skill.id, {
        onSuccess: (forkedSkill) => {
          updateSkillMutation.mutate(
            {
              id: forkedSkill.id,
              name: trimmedName,
              description: trimmedDescription,
              skillContent: trimmedSkillContent || null,
              skillType: 'workflow',
            },
            { onSuccess: () => closeModal('skill-editor') },
          )
        },
      })
    } else if (isUpdate && skill?.id) {
      updateSkillMutation.mutate(
        {
          id: skill.id,
          name: trimmedName,
          description: trimmedDescription,
          skillContent: trimmedSkillContent || null,
          skillType: 'workflow',
        },
        { onSuccess: () => closeModal('skill-editor') },
      )
    } else {
      // Covers paths into the modal that bypass the hidden Create buttons; the
      // server refuses the create for these plans anyway.
      if (!createCustomSkills) {
        closeModal('skill-editor')
        return
      }
      createSkillMutation.mutate(
        {
          name: trimmedName,
          description: trimmedDescription,
          skillContent: trimmedSkillContent || undefined,
          skillType: 'workflow',
          public: false,
          // New skills default to chat-only auto-suggestion; users can
          // opt them into the meeting planner from Skill Triggers later.
          availability: { audioPlanner: false, agents: true, userInvoke: true },
        },
        { onSuccess: () => closeModal('skill-editor') },
      )
    }
  }

  const saveDisabled = useMemo(() => {
    return !name.trim() || !skillContent.trim()
  }, [name, skillContent])

  const isSaving = createSkillMutation.isPending || updateSkillMutation.isPending || forkSkillMutation.isPending

  const title = isEditingDefault ? `Customize Action: ${skill?.name}` : isUpdate ? 'Edit Action' : 'Create Action'

  return (
    <Modal>
      <Modal.Content className="w-full max-w-[min(720px,92vw)]" showClose={false}>
        <Modal.Header>
          <Modal.Title>{title}</Modal.Title>
          {isEditingDefault && (
            <div className="text-foreground-placeholder mt-1 flex items-center gap-1.5 text-xs">
              <GitFork className="size-3 shrink-0" />
              <span>Saving creates a personal version that overrides the default.</span>
            </div>
          )}
        </Modal.Header>

        <Modal.Body className="max-h-[60vh]">
          <FieldGroup label="Name">
            <Input.Field
              placeholder="e.g. Linear Ticket with Acceptance Criteria"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </FieldGroup>

          <FieldGroup label="When to Use this" hint="Helps the action planner decide when to trigger this action">
            <Textarea.Field
              placeholder="Describe when this action should be used and what kind of output it should produce."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
            />
          </FieldGroup>

          <FieldGroup label="Workflow Instructions" hint="Main instructions Highlight uses when drafting this action">
            <AutoResizeTextarea
              placeholder="Example: Draft a Linear ticket with a clear title, concise summary, explicit acceptance criteria, and a short implementation plan."
              value={skillContent}
              onChange={(e) => setSkillContent(e.target.value)}
            />
          </FieldGroup>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="outline" onClick={() => closeModal('skill-editor')}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={saveDisabled || isSaving} loading={isSaving}>
            {isEditingDefault ? 'Fork & Save' : isUpdate ? 'Save Changes' : 'Create Action'}
          </Button>
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  )
}
